<script lang="ts">
    import mapboxgl from "mapbox-gl";

    import { onMount } from "svelte";
    import { ArrowRightIcon, CircleIcon, HomeIcon, MapPinIcon, UserIcon } from "svelte-feather-icons";
    import { fade } from "svelte/transition";
    import { formatDuration, titleCase } from "../formatting.js";
    import type { SearchResult } from "../search.js";
    import { mapbox } from "./mapbox.js";
    import { TransportMode } from "../transport_mode.js";
    export let origin_id: number | null;
    export let origin_name: string | null;
    export let resort: SearchResult;
    export let transport_mode: TransportMode | null;

    let container: HTMLElement;
    let map: mapbox.Map;
    let directions: any = null;
    let has_drawn_directions = false;
    let destinationMarker: mapbox.Marker | null = null;

    export function resize() {
        map.resize();
    }

    const drawDirections = () => {
        if (!map) {
            return;
        }

        if (map.isStyleLoaded()) {
            has_drawn_directions = true;

            if (destinationMarker !== null) destinationMarker.remove();
            destinationMarker = new mapboxgl.Marker({
                color: "#2289d3",
            })
                .setLngLat(resort.location)
                .addTo(map);

            if (map.getLayer("directions_outline")) map.removeLayer("directions_outline");
            if (map.getSource("directions")) map.removeSource("directions");

            if (directions !== null) {
                map.addSource("directions", {
                    type: "geojson",
                    data: {
                        type: "Feature",
                        geometry: directions.itinerary,
                        properties: null,
                    },
                });
                map.addLayer({
                    id: "directions_outline",
                    type: "line",
                    source: "directions",
                    layout: {
                        "line-cap": "round",
                        "line-join": "round",
                    },
                    paint: {
                        "line-color": "#2289d3",
                        "line-width": 5,
                    },
                });
            }
        }

        const sweden_bounds = [
            [13.782, 55.755],
            [18.789, 59.74],
            [11.938, 59.243],
            [20.359, 68.99],
        ];

        // Set the camera even if the style is not loaded.
        // This avoids the first frame being painted in the wrong position.
        const coordinates = directions && directions.itinerary ? directions.itinerary.coordinates : sweden_bounds;
        let lng_lo = coordinates[0][0];
        let lng_hi = lng_lo;
        let lat_lo = coordinates[0][1];
        let lat_hi = lat_lo;
        for (const coordinate of coordinates) {
            lng_lo = Math.min(lng_lo, coordinate[0]);
            lng_hi = Math.max(lng_hi, coordinate[0]);
            lat_lo = Math.min(lat_lo, coordinate[1]);
            lat_hi = Math.max(lat_hi, coordinate[1]);
        }
        // Prevent zooming in too far
        const min_diff = 0.1;
        const lng_diff = Math.max(lng_hi - lng_lo, min_diff);
        const lat_diff = Math.max(lat_hi - lat_lo, min_diff);
        const margin_size = directions !== null ? 0.2 : 0.1;
        const margin_size_top = directions !== null ? 0.4 : 0.1;
        const bounds = map.cameraForBounds([
            [lng_lo - lng_diff * margin_size, lat_lo - lat_diff * margin_size],
            [lng_hi + lng_diff * margin_size, lat_hi + lat_diff * margin_size_top],
        ]);
        if (bounds !== undefined) {
            map.jumpTo(bounds);
        }
    };

    let directionsURL = "";

    $: {
        if (origin_id !== null && transport_mode !== null) {
            const url = "/directions?origin=" + origin_id + "&resort=" + resort.id + "&mode=" + transport_mode;
            // Avoid making unnecessary fetch requests
            if (directionsURL != url) {
                directionsURL = url;
                fetch(directionsURL, {
                    method: "GET",
                }).then(async (r) => {
                    directions = await r.json();
                    drawDirections();
                });
            }
        }
    }

    onMount(() => {
        map = new mapbox.Map({
            container,
            style: "mapbox://styles/gullesnuffs/cksa9bdow4qch17qplc2s6h79",
            zoom: 3,
            center: [16, 62],
            trackResize: false,
        });
        map.on("load", function () {
            drawDirections();
        });
        return () => {
            map.remove();
        };
    });
</script>

<div class="map-wrapper">
    <div bind:this={container} class="map">
        {#if map}
            <slot />
        {/if}
    </div>
    <div class="cover" class:visible={!has_drawn_directions} />
    <div class="endpoints">
        {#if origin_name !== null}
            <div><UserIcon />{origin_name}</div>
        {/if}
        <div><ArrowRightIcon />{titleCase(resort.name)}</div>
        {#if origin_id !== null && transport_mode && directions && directions.travel_time_in_seconds !== null}
            <div class="distance" class:outside_range={directions !== null && !directions.travel_time_within_range}>
                {#if transport_mode == TransportMode.Car}
                    <!-- svelte-ignore a11y-missing-attribute -->
                    <img src="/static/car.svg" />
                {:else}
                    <!-- svelte-ignore a11y-missing-attribute -->
                    <img src="/static/train.svg" />
                {/if}
                {directions ? formatDuration(directions.travel_time_in_seconds) : "?"}
            </div>
        {/if}
    </div>
</div>

<style>
    .map {
        width: 100%;
        height: 100%;
    }
    .map-wrapper {
        position: relative;
    }

    .endpoints {
        position: absolute;
        top: 15px;
        right: 10px;
        left: 10px;
        font-size: 18px;
        display: flex;
        flex-direction: column;
        background: white;
        border-radius: 8px;
        box-shadow: 0px 0.1px 0.3px rgba(0, 0, 0, 0.042), 0px 0.3px 0.7px rgba(0, 0, 0, 0.061),
            0px 0.5px 1.3px rgba(0, 0, 0, 0.075), 0px 0.9px 2.2px rgba(0, 0, 0, 0.089),
            0px 1.7px 4.2px rgba(0, 0, 0, 0.108), 0px 4px 10px rgba(0, 0, 0, 0.15);
    }

    .endpoints div {
        padding: 8px 10px;
        display: flex;
        flex-direction: row;
        justify-content: left;
        align-items: center;
    }

    .endpoints div:nth-child(2),
    .endpoints div:nth-child(3) {
        border-top: 1px solid rgb(220, 220, 220);
    }

    :global(.endpoints svg, .endpoints img) {
        width: 24px;
        height: 24px;
        margin-left: 5px;
        margin-right: 5px;
    }
    .cover {
        position: absolute;
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0px;
        background: #f9efdc;
        opacity: 0;
        transition: opacity 800ms;
        pointer-events: none;
    }

    .cover.visible {
        opacity: 1;
    }
</style>
